import { useCallback, useRef } from 'react';
import { clampWidth } from './column-width';

interface ColumnResizerProps {
  /** Current width of the column this resizer sizes, in pixels. */
  width: number;
  onWidthChange: (width: number) => void;
  min: number;
  max: number;
  label: string;
  /**
   * Which neighbour the width belongs to. The other one is the elastic column
   * that takes whatever is left, so this says which way "wider" is.
   */
  side: 'left' | 'right';
}

/** How far one arrow-key press moves the divider. */
const KEYBOARD_STEP = 24;

/**
 * The draggable divider between two columns — the window-splitter pattern, so
 * it is a focusable `separator` that the arrow keys move as well as the pointer.
 *
 * It sizes one named neighbour (`side`); the other is the elastic column that
 * absorbs whatever is left. Dragging *towards* the sized column narrows it and
 * gives the room to its neighbour.
 */
export function ColumnResizer({ width, onWidthChange, min, max, label, side }: ColumnResizerProps) {
  // Where the drag began, and how wide the column was then. A ref because the
  // move handler reads them as the pointer travels, not as it was set up.
  const dragRef = useRef<{ x: number; width: number } | null>(null);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      dragRef.current = { x: event.clientX, width };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [width]
  );

  // A column on the left grows as the pointer travels right; one on the right
  // grows as it travels left. Everything below works in "wider" rather than in
  // screen pixels, so the sign lives here alone.
  const growth = useCallback(
    (pixels: number) => (side === 'left' ? pixels : -pixels),
    [side]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;

      onWidthChange(clampWidth(drag.width + growth(event.clientX - drag.x), min, max));
    },
    [onWidthChange, min, max, growth]
  );

  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const travel = event.key === 'ArrowRight' ? KEYBOARD_STEP : event.key === 'ArrowLeft' ? -KEYBOARD_STEP : 0;
      if (travel === 0) return;

      event.preventDefault();
      onWidthChange(clampWidth(width + growth(travel), min, max));
    },
    [width, onWidthChange, min, max, growth]
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(width)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      data-testid="column-resizer"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
      // A hairline that thickens under the pointer: the grab area has to be
      // wider than the line it draws, or it is a two-pixel target.
      className="group relative hidden cursor-col-resize touch-none select-none lg:block focus-visible:outline-none"
    >
      <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-colors group-hover:bg-primary group-focus-visible:bg-primary" />
    </div>
  );
}
